import type { ImageMetadata, ListImagesResponse, ApiError } from '../types/image';
import { getAuthHeaders } from './authHeaders';

/**
 * Gallery API service for interacting with the backend
 */

// API base URL - must be set via environment
const API_BASE_URL = import.meta.env.VITE_API_ENDPOINT;
if (!API_BASE_URL) {
  throw new Error('Missing required environment variable: VITE_API_ENDPOINT');
}

/**
 * List images for a user
 * @param userId - User ID to list images for
 * @param limit - Maximum number of images to return (default: 50)
 * @param cursor - Pagination cursor for fetching next page
 * @returns Promise with list of images
 */
export async function listImages(
  userId: string,
  limit: number = 50,
  cursor?: string
): Promise<ListImagesResponse> {
  const params = new URLSearchParams({ limit: String(limit) });
  if (cursor) {
    params.append('cursor', cursor);
  }

  const headers = await getAuthHeaders();
  const response = await fetch(
    `${API_BASE_URL}/gallery/${encodeURIComponent(userId)}?${params}`,
    {
      method: 'GET',
      headers,
    }
  );

  if (!response.ok) {
    const error: ApiError = await response.json();
    throw new Error(error.error?.message || 'Failed to list images');
  }

  return response.json();
}

/**
 * Get metadata for a single image
 * @param userId - User ID (for authorization)
 * @param mediaId - Media ID to get metadata for
 * @returns Promise with image metadata
 */
export async function getImageMetadata(
  userId: string,
  mediaId: string
): Promise<ImageMetadata> {
  const headers = await getAuthHeaders();
  const response = await fetch(
    `${API_BASE_URL}/gallery/${encodeURIComponent(userId)}/${encodeURIComponent(mediaId)}`,
    {
      method: 'GET',
      headers,
    }
  );

  if (!response.ok) {
    const error: ApiError = await response.json();
    throw new Error(error.error?.message || 'Failed to get image metadata');
  }

  return response.json();
}

/**
 * Delete an image
 * @param userId - User ID (for authorization)
 * @param mediaId - Media ID to delete
 * @returns Promise that resolves when deleted
 */
export async function deleteImage(
  userId: string,
  mediaId: string
): Promise<{ deleted: boolean; mediaId: string }> {
  const headers = await getAuthHeaders();
  const response = await fetch(
    `${API_BASE_URL}/gallery/${encodeURIComponent(userId)}/${encodeURIComponent(mediaId)}`,
    {
      method: 'DELETE',
      headers,
    }
  );

  if (!response.ok) {
    const error: ApiError = await response.json();
    throw new Error(error.error?.message || 'Failed to delete image');
  }

  return response.json();
}

/**
 * Generate a transformation URL for an image
 * @param userId - User ID (client ID)
 * @param mediaId - Media ID
 * @param transformations - Transformation string (e.g., "w300-h200-resize")
 * @param useCdn - Whether to use CDN URL (default: true)
 * @returns Transformation URL
 */
export function getTransformUrl(
  userId: string,
  mediaId: string,
  transformations: string,
  useCdn: boolean = true
): string {
  const cdnUrl = import.meta.env.VITE_MM_CDN_URL;
  const baseUrl = useCdn && cdnUrl ? cdnUrl : API_BASE_URL;

  // Format: /image/{userId}/{mediaId}-{transformations}.webp
  return `${baseUrl}/image/${encodeURIComponent(userId)}/${encodeURIComponent(mediaId)}-${transformations}.webp`;
}

/**
 * Get a presigned URL for uploading an image
 * @param userId - User ID (username)
 * @param filename - Original filename
 * @param contentType - MIME type of the file
 * @returns Promise with presigned upload URL
 */
export async function getUploadUrl(
  userId: string,
  filename: string,
  contentType: string
): Promise<{ uploadUrl: string; uploadFields: Record<string, string>; uploadKey: string; expiresIn: number; maxSizeBytes: number }> {
  const headers = await getAuthHeaders();
  const response = await fetch(
    `${API_BASE_URL}/gallery/${encodeURIComponent(userId)}`,
    {
      method: 'POST',
      headers,
      body: JSON.stringify({ filename, contentType }),
    }
  );

  if (!response.ok) {
    const error: ApiError = await response.json();
    throw new Error(error.error?.message || 'Failed to get upload URL');
  }

  return response.json();
}

/**
 * Upload a file using a presigned POST
 * @param uploadUrl - Presigned S3 POST URL
 * @param uploadFields - Presigned POST fields (policy, signature, etc.)
 * @param file - File to upload
 * @param onProgress - Progress callback (0-100)
 * @returns Promise that resolves when upload is complete
 */
export async function uploadFile(
  uploadUrl: string,
  uploadFields: Record<string, string>,
  file: File,
  onProgress?: (percent: number) => void
): Promise<void> {
  return new Promise((resolve, reject) => {
    const formData = new FormData();
    // Presigned POST fields must come before the file
    for (const [key, value] of Object.entries(uploadFields)) {
      formData.append(key, value);
    }
    formData.append('file', file);

    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && onProgress) {
        const percent = Math.round((event.loaded / event.total) * 100);
        onProgress(percent);
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}`));
      }
    });

    xhr.addEventListener('error', () => {
      reject(new Error('Upload failed'));
    });

    xhr.open('POST', uploadUrl);
    xhr.send(formData);
  });
}
